import { ModerationType } from '../const/ModerationTypes'

export const ORGANIZATIONS_ENTITY_TYPE = 'organizations' as const

export const isOrganizationsEntityType = (entityType?: string): boolean => (
  entityType === ORGANIZATIONS_ENTITY_TYPE
)

type ResolveOrganizationsContextParams = {
  mainFilterContexts?: unknown
  teamContextName?: string
}

const normalizeContextNames = (raw: unknown): string[] => {
  if (!raw) return []
  const arr = Array.isArray(raw) ? raw : [raw]
  return arr.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

const dedupeContextNames = (names: string[]): string[] => {
  const seen = new Set<string>()
  return names.filter((name) => {
    if (!name || seen.has(name)) return false
    seen.add(name)
    return true
  })
}

export const resolveOrganizationsContextNames = ({
  mainFilterContexts,
  teamContextName,
}: ResolveOrganizationsContextParams): string[] => {
  const mainContexts = normalizeContextNames(mainFilterContexts)
  if (mainContexts.length) {
    return mainContexts
  }

  return normalizeContextNames(teamContextName)
}

/** Параметры контекста для Team/GetList при customSorting организаций — только contextNames. */
export const buildOrganizationsTeamGetListContextParams = (
  contextNames: string[],
): { contextNames: string[] } | Record<string, never> => {
  const unifiedContextNames = dedupeContextNames(contextNames)

  if (!unifiedContextNames.length) {
    return {}
  }

  return { contextNames: unifiedContextNames }
}

export const resolveOrganizationsContextName = ({
  mainFilterContexts,
  teamContextName,
}: ResolveOrganizationsContextParams): string => (
  resolveOrganizationsContextNames({
    mainFilterContexts,
    teamContextName,
  })[0] || ''
)

type PrepareOrganizationsGridFilterOptions = {
  teamContextName?: string
  mainFilterContexts?: unknown
  parentTeamContextNames?: string[]
}

type TeamLikeForOrganizationsCatalogGrid = {
  moderationType?: number
  mainEntityType?: string
  isProjectsView?: boolean
  customSorting?: boolean
  customProjectSorting?: string[]
  customTeamsSorting?: string[]
  isShowTabs?: boolean
  contexts?: string[]
  parentTeam?: {
    contextName?: string
    contexts?: string[]
  }
}

const hasOuterManualOrganizationsCatalogSelection = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
): boolean => (
  (team?.customTeamsSorting?.length ?? 0) > 0
  && isOrganizationsEntityType(team?.mainEntityType)
  && !team?.customSorting
)

export const isOuterShowcaseOrganizationsCatalogMode = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
  entityType?: string,
): boolean => {
  if (team?.isShowTabs) {
    return false
  }

  if (team?.moderationType !== ModerationType.Outer) {
    return false
  }

  if (team.isProjectsView === false && !hasOuterManualOrganizationsCatalogSelection(team)) {
    return false
  }

  const resolvedEntityType = entityType ?? team?.mainEntityType
  if (!isOrganizationsEntityType(resolvedEntityType)) {
    return false
  }

  return team.isProjectsView === true
    || hasOuterManualOrganizationsCatalogSelection(team)
}

export const resolveTeamShowcaseEntityType = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
  fallbackEntityType?: string,
): string | undefined => {
  const fallback = fallbackEntityType ?? team?.mainEntityType

  if (team?.isShowTabs) {
    return fallback
  }

  if (team?.moderationType !== ModerationType.Outer) {
    return fallback
  }

  if (team.isProjectsView === false && !hasOuterManualOrganizationsCatalogSelection(team)) {
    if (isOrganizationsEntityType(fallback)) {
      return 'projects'
    }
    return fallback || 'projects'
  }

  const isOrganizationsCatalog = isOuterShowcaseOrganizationsCatalogMode(team, fallbackEntityType)

  if (!isOrganizationsCatalog) {
    return fallback
  }

  return ORGANIZATIONS_ENTITY_TYPE
}

export const resolveGridEntityType = (
  team: TeamLikeForOrganizationsCatalogGrid | null | undefined,
  fallbackEntityType?: string,
  isTabRequest?: boolean,
): string | undefined => {
  if (isTabRequest) {
    return fallbackEntityType ?? team?.mainEntityType
  }

  return resolveTeamShowcaseEntityType(team, fallbackEntityType)
}

export const isOrganizationsCatalogCustomSorting = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
  entityType?: string,
): boolean => (
  isOrganizationsEntityType(resolveTeamShowcaseEntityType(team, entityType))
)

export const resolveExternalOrganizationsCatalogGridOptions = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
): Pick<PrepareOrganizationsGridFilterOptions, 'parentTeamContextNames'> => {
  if (!team) {
    return {}
  }

  if (team.moderationType !== ModerationType.Outer) {
    return {}
  }

  if (!isOrganizationsCatalogCustomSorting(team)) {
    return {}
  }

  const parentContextNames = team.contexts?.length
    ? normalizeContextNames(team.contexts)
    : resolveOrganizationsContextNames({
      mainFilterContexts: team.parentTeam?.contexts,
      teamContextName: team.parentTeam?.contextName,
    })

  return {
    ...(parentContextNames.length ? { parentTeamContextNames: parentContextNames } : {}),
  }
}

export const buildOrganizationsGridFilterOptions = (
  teamContextName: string | undefined,
  mainFilterContexts: unknown,
  team?: TeamLikeForOrganizationsCatalogGrid | null,
): PrepareOrganizationsGridFilterOptions => ({
  teamContextName,
  mainFilterContexts,
  ...resolveExternalOrganizationsCatalogGridOptions(team),
})

type TabOrganizationsCatalogCarrier = {
  isProjectsView?: boolean
  entityType?: string
  customTeamsSorting?: string[]
  customSorting?: boolean
}

export const isTabOrganizationsCatalogMode = (
  tab?: TabOrganizationsCatalogCarrier | null,
  entityType?: string,
  moderationType?: unknown,
): boolean => {
  if (moderationType !== ModerationType.Outer) {
    return false
  }

  if (!tab || tab.customSorting) {
    return false
  }

  const resolvedEntityType = entityType ?? tab.entityType
  if (!isOrganizationsEntityType(resolvedEntityType)) {
    return false
  }

  return !!tab.isProjectsView || (tab.customTeamsSorting?.length ?? 0) > 0
}

export const applyShowcaseOrganizationsCatalogEmptySelectionGuard = (
  finalFilter: Record<string, unknown>,
  {
    team,
    entityType,
    moderationType,
    isNoTabs,
  }: {
    team?: TeamLikeForOrganizationsCatalogGrid | null
    entityType?: string
    moderationType?: unknown
    isNoTabs: boolean
  },
): Record<string, unknown> => {
  if (!isNoTabs || moderationType !== ModerationType.Outer) {
    return finalFilter
  }

  if (!isOuterShowcaseOrganizationsCatalogMode(team, entityType)) {
    return finalFilter
  }

  const hasConfiguredOrganizations = (team?.customTeamsSorting?.length ?? 0) > 0
  if (hasConfiguredOrganizations) {
    return finalFilter
  }

  const parentContextNames = resolveExternalOrganizationsCatalogGridOptions(team).parentTeamContextNames ?? []
  const contextParams = buildOrganizationsTeamGetListContextParams(parentContextNames)

  if (!contextParams.contextNames?.length) {
    return {
      limit: finalFilter.limit,
      sort: finalFilter.sort,
      usingFilterOrManual: false,
      projectIds: [],
    }
  }

  return {
    limit: finalFilter.limit,
    sort: finalFilter.sort,
    usingFilterOrManual: true,
    ...contextParams,
  }
}

export const applyTabOrganizationsCatalogEmptySelectionGuard = (
  finalFilter: Record<string, unknown>,
  {
    tab,
    team,
    entityType,
    moderationType,
    isTabRequest,
  }: {
    tab?: TabOrganizationsCatalogCarrier
    team?: TeamLikeForOrganizationsCatalogGrid | null
    entityType?: string
    moderationType?: unknown
    isTabRequest: boolean
  },
): Record<string, unknown> => {
  if (!isTabRequest || moderationType !== ModerationType.Outer || !isTabOrganizationsCatalogMode(tab, entityType || tab?.entityType, moderationType)) {
    return finalFilter
  }

  const resolvedEntityType = entityType || tab?.entityType
  if (!isOrganizationsEntityType(resolvedEntityType)) {
    return finalFilter
  }

  const hasConfiguredOrganizations = (tab.customTeamsSorting?.length ?? 0) > 0
  if (hasConfiguredOrganizations) {
    return finalFilter
  }

  const parentContextNames = resolveExternalOrganizationsCatalogGridOptions(team).parentTeamContextNames ?? []
  const contextParams = buildOrganizationsTeamGetListContextParams(parentContextNames)

  if (!contextParams.contextNames?.length) {
    return {
      limit: finalFilter.limit,
      sort: finalFilter.sort,
      usingFilterOrManual: false,
      projectIds: [],
    }
  }

  return {
    limit: finalFilter.limit,
    sort: finalFilter.sort,
    usingFilterOrManual: true,
    ...contextParams,
  }
}

export const prepareCustomSortingGridFilter = (
  finalFilter: Record<string, unknown>,
  entityType?: string,
  options: PrepareOrganizationsGridFilterOptions = {},
): Record<string, unknown> => {
  if (!isOrganizationsEntityType(entityType) || !finalFilter.customSorting) {
    return finalFilter
  }

  const contextNames = options.parentTeamContextNames?.length
    ? dedupeContextNames(options.parentTeamContextNames)
    : resolveOrganizationsContextNames({
      mainFilterContexts: options.mainFilterContexts,
      teamContextName: options.teamContextName,
    })

  const {
    contexts,
    contextName,
    contextNames: _contextNames,
    requestContext,
    projectStatus,
    projectIds,
    projectFilter,
    onlyBooks,
    teamId,
    designerId,
    designerIds,
    ...rest
  } = finalFilter

  return {
    ...rest,
    customSorting: true,
    ...buildOrganizationsTeamGetListContextParams(contextNames),
  }
}
import { ModerationType } from '../const/ModerationTypes'

export const ORGANIZATIONS_ENTITY_TYPE = 'organizations' as const

export const isOrganizationsEntityType = (entityType?: string): boolean => (
  entityType === ORGANIZATIONS_ENTITY_TYPE
)

type ResolveOrganizationsContextParams = {
  mainFilterContexts?: unknown
  teamContextName?: string
}

const normalizeContextNames = (raw: unknown): string[] => {
  if (!raw) return []
  const arr = Array.isArray(raw) ? raw : [raw]
  return arr.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

const dedupeContextNames = (names: string[]): string[] => {
  const seen = new Set<string>()
  return names.filter((name) => {
    if (!name || seen.has(name)) return false
    seen.add(name)
    return true
  })
}

export const resolveOrganizationsContextNames = ({
  mainFilterContexts,
  teamContextName,
}: ResolveOrganizationsContextParams): string[] => {
  const mainContexts = normalizeContextNames(mainFilterContexts)
  if (mainContexts.length) {
    return mainContexts
  }

  return normalizeContextNames(teamContextName)
}

/** Параметры контекста для Team/GetList при customSorting организаций — только contextNames. */
export const buildOrganizationsTeamGetListContextParams = (
  contextNames: string[],
): { contextNames: string[] } | Record<string, never> => {
  const unifiedContextNames = dedupeContextNames(contextNames)

  if (!unifiedContextNames.length) {
    return {}
  }

  return { contextNames: unifiedContextNames }
}

export const resolveOrganizationsContextName = ({
  mainFilterContexts,
  teamContextName,
}: ResolveOrganizationsContextParams): string => (
  resolveOrganizationsContextNames({
    mainFilterContexts,
    teamContextName,
  })[0] || ''
)

type PrepareOrganizationsGridFilterOptions = {
  teamContextName?: string
  mainFilterContexts?: unknown
  parentTeamContextNames?: string[]
}

type TeamLikeForOrganizationsCatalogGrid = {
  moderationType?: number
  mainEntityType?: string
  isProjectsView?: boolean
  customSorting?: boolean
  customProjectSorting?: string[]
  customTeamsSorting?: string[]
  isShowTabs?: boolean
  contexts?: string[]
  parentTeam?: {
    contextName?: string
    contexts?: string[]
  }
}

const hasOuterManualOrganizationsCatalogSelection = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
): boolean => (
  (team?.customTeamsSorting?.length ?? 0) > 0
  && isOrganizationsEntityType(team?.mainEntityType)
  && !team?.customSorting
)

export const isOuterShowcaseOrganizationsCatalogMode = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
  entityType?: string,
): boolean => {
  if (team?.isShowTabs) {
    return false
  }

  if (team?.moderationType !== ModerationType.Outer) {
    return false
  }

  if (team.isProjectsView === false && !hasOuterManualOrganizationsCatalogSelection(team)) {
    return false
  }

  const resolvedEntityType = entityType ?? team?.mainEntityType
  if (!isOrganizationsEntityType(resolvedEntityType)) {
    return false
  }

  return team.isProjectsView === true
    || hasOuterManualOrganizationsCatalogSelection(team)
}

export const resolveTeamShowcaseEntityType = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
  fallbackEntityType?: string,
): string | undefined => {
  const fallback = fallbackEntityType ?? team?.mainEntityType

  if (team?.isShowTabs) {
    return fallback
  }

  if (team?.moderationType !== ModerationType.Outer) {
    return fallback
  }

  if (team.isProjectsView === false && !hasOuterManualOrganizationsCatalogSelection(team)) {
    if (isOrganizationsEntityType(fallback)) {
      return 'projects'
    }
    return fallback || 'projects'
  }

  const isOrganizationsCatalog = isOuterShowcaseOrganizationsCatalogMode(team, fallbackEntityType)

  if (!isOrganizationsCatalog) {
    return fallback
  }

  return ORGANIZATIONS_ENTITY_TYPE
}

export const resolveGridEntityType = (
  team: TeamLikeForOrganizationsCatalogGrid | null | undefined,
  fallbackEntityType?: string,
  isTabRequest?: boolean,
): string | undefined => {
  if (isTabRequest) {
    return fallbackEntityType ?? team?.mainEntityType
  }

  return resolveTeamShowcaseEntityType(team, fallbackEntityType)
}

export const isOrganizationsCatalogCustomSorting = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
  entityType?: string,
): boolean => (
  isOrganizationsEntityType(resolveTeamShowcaseEntityType(team, entityType))
)

export const resolveExternalOrganizationsCatalogGridOptions = (
  team?: TeamLikeForOrganizationsCatalogGrid | null,
): Pick<PrepareOrganizationsGridFilterOptions, 'parentTeamContextNames'> => {
  if (!team) {
    return {}
  }

  if (team.moderationType !== ModerationType.Outer) {
    return {}
  }

  if (!isOrganizationsCatalogCustomSorting(team)) {
    return {}
  }

  const parentContextNames = team.contexts?.length
    ? normalizeContextNames(team.contexts)
    : resolveOrganizationsContextNames({
      mainFilterContexts: team.parentTeam?.contexts,
      teamContextName: team.parentTeam?.contextName,
    })

  return {
    ...(parentContextNames.length ? { parentTeamContextNames: parentContextNames } : {}),
  }
}

export const buildOrganizationsGridFilterOptions = (
  teamContextName: string | undefined,
  mainFilterContexts: unknown,
  team?: TeamLikeForOrganizationsCatalogGrid | null,
): PrepareOrganizationsGridFilterOptions => ({
  teamContextName,
  mainFilterContexts,
  ...resolveExternalOrganizationsCatalogGridOptions(team),
})

type TabOrganizationsCatalogCarrier = {
  isProjectsView?: boolean
  entityType?: string
  customTeamsSorting?: string[]
  customSorting?: boolean
}

export const isTabOrganizationsCatalogMode = (
  tab?: TabOrganizationsCatalogCarrier | null,
  entityType?: string,
  moderationType?: unknown,
): boolean => {
  if (moderationType !== ModerationType.Outer) {
    return false
  }

  if (!tab || tab.customSorting) {
    return false
  }

  const resolvedEntityType = entityType ?? tab.entityType
  if (!isOrganizationsEntityType(resolvedEntityType)) {
    return false
  }

  return !!tab.isProjectsView || (tab.customTeamsSorting?.length ?? 0) > 0
}

export const applyShowcaseOrganizationsCatalogEmptySelectionGuard = (
  finalFilter: Record<string, unknown>,
  {
    team,
    entityType,
    moderationType,
    isNoTabs,
  }: {
    team?: TeamLikeForOrganizationsCatalogGrid | null
    entityType?: string
    moderationType?: unknown
    isNoTabs: boolean
  },
): Record<string, unknown> => {
  if (!isNoTabs || moderationType !== ModerationType.Outer) {
    return finalFilter
  }

  if (!isOuterShowcaseOrganizationsCatalogMode(team, entityType)) {
    return finalFilter
  }

  const hasConfiguredOrganizations = (team?.customTeamsSorting?.length ?? 0) > 0
  if (hasConfiguredOrganizations) {
    return finalFilter
  }

  const parentContextNames = resolveExternalOrganizationsCatalogGridOptions(team).parentTeamContextNames ?? []
  const contextParams = buildOrganizationsTeamGetListContextParams(parentContextNames)

  if (!contextParams.contextNames?.length) {
    return {
      limit: finalFilter.limit,
      sort: finalFilter.sort,
      usingFilterOrManual: false,
      projectIds: [],
    }
  }

  return {
    limit: finalFilter.limit,
    sort: finalFilter.sort,
    usingFilterOrManual: true,
    ...contextParams,
  }
}

export const applyTabOrganizationsCatalogEmptySelectionGuard = (
  finalFilter: Record<string, unknown>,
  {
    tab,
    team,
    entityType,
    moderationType,
    isTabRequest,
  }: {
    tab?: TabOrganizationsCatalogCarrier
    team?: TeamLikeForOrganizationsCatalogGrid | null
    entityType?: string
    moderationType?: unknown
    isTabRequest: boolean
  },
): Record<string, unknown> => {
  if (!isTabRequest || moderationType !== ModerationType.Outer || !isTabOrganizationsCatalogMode(tab, entityType || tab?.entityType, moderationType)) {
    return finalFilter
  }

  const resolvedEntityType = entityType || tab?.entityType
  if (!isOrganizationsEntityType(resolvedEntityType)) {
    return finalFilter
  }

  const hasConfiguredOrganizations = (tab.customTeamsSorting?.length ?? 0) > 0
  if (hasConfiguredOrganizations) {
    return finalFilter
  }

  const parentContextNames = resolveExternalOrganizationsCatalogGridOptions(team).parentTeamContextNames ?? []
  const contextParams = buildOrganizationsTeamGetListContextParams(parentContextNames)

  if (!contextParams.contextNames?.length) {
    return {
      limit: finalFilter.limit,
      sort: finalFilter.sort,
      usingFilterOrManual: false,
      projectIds: [],
    }
  }

  return {
    limit: finalFilter.limit,
    sort: finalFilter.sort,
    usingFilterOrManual: true,
    ...contextParams,
  }
}

export const prepareCustomSortingGridFilter = (
  finalFilter: Record<string, unknown>,
  entityType?: string,
  options: PrepareOrganizationsGridFilterOptions = {},
): Record<string, unknown> => {
  if (!isOrganizationsEntityType(entityType) || !finalFilter.customSorting) {
    return finalFilter
  }

  const contextNames = options.parentTeamContextNames?.length
    ? dedupeContextNames(options.parentTeamContextNames)
    : resolveOrganizationsContextNames({
      mainFilterContexts: options.mainFilterContexts,
      teamContextName: options.teamContextName,
    })

  const {
    contexts,
    contextName,
    contextNames: _contextNames,
    requestContext,
    projectStatus,
    projectIds,
    projectFilter,
    onlyBooks,
    teamId,
    designerId,
    designerIds,
    ...rest
  } = finalFilter

  return {
    ...rest,
    customSorting: true,
    ...buildOrganizationsTeamGetListContextParams(contextNames),
  }
}
